import { OneBoxPage, useCurrentTenant } from "@drift/oneplatfront";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLayout, useTranslation } from "@onefront/react-sdk";
import { useWsGet } from "@onefront/workspace-forms";
import { ExtendedTypography } from "@vapor/react-extended";
import { Box, Button, Grid, useTheme } from "@vapor/react-material";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IntermediarySections } from "../../../../@types/enums/intermediaries/IntermediariesSections";
import { useGetWorkspaceDetails } from "../../../../hooks/api/details/useGetWorkspaceDetails";
import { mapEnum } from "../../../../utils/functions/generic";
import DetailsSidebar from "../../../details/components/details/sidebar";
import { SidebarSkeleton } from "../../../details/components/skeletons/sidebar";
import { IntermediaryStepForm } from "../../../tenant/components/settings/intermediary/components/intermediary-step-form/IntermediaryStepForm";
import DetailsIntermediary from "./details";

export const intermediarySections = t =>
    mapEnum(IntermediarySections, x => {
        return {
            key: x,
            label: t(`step.intermediary.${x}`)
        };
    });

interface IWorkspaceIntermediarySettings {
    view: "CREATE" | "EDIT";
}

export const WorkspaceIntermediarySettings = ({ view }: IWorkspaceIntermediarySettings) => {
    const {
        palette: {
            primary: { background: WhiteSmoke }
        }
    } = useTheme();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { workspaceId, intermediaryTaxId, step } = useParams();
    const { data, loading } = useGetWorkspaceDetails(workspaceId);
    const [intermediary, setIntermediary] = useState<any>(null);

    const tenant = useCurrentTenant();
    const { isEmbed } = useLayout();

    const intermediaryUrl = useMemo(() => {
        return `rnd:core:intermediary-registry-read:api://v1/${tenant?.itemId}/intermediary/${intermediaryTaxId}`;
    }, [intermediaryTaxId, tenant?.itemId]);

    const {
        loading: loadingIntermediary,
        fetch: fetchIntermediary,
        startPolling,
        stopPolling
    } = useWsGet(intermediaryUrl, {}) as any;

    React.useEffect(() => {
        if (intermediaryTaxId && step === "data") {
            fetchIntermediary({
                params: { withCredentials: true }
            }).then(res => {
                setIntermediary(res?.data);
            });
        }
        //eslint-disable-next-line
    }, [intermediaryTaxId, view, step]);

    return (
        <OneBoxPage
            showCustomerSelector={false}
            headerLeft={
                <Button onClick={() => navigate(`../details/${workspaceId}/intermediaries`)}>
                    <ExtendedTypography color="primary.interactiveDefault">
                        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("intermediaries.back")}
                    </ExtendedTypography>
                </Button>
            }
            headerRight={<Box width={200}>&nbsp;</Box>}
            title={
                view === "EDIT"
                    ? t("intermediaries.edit.title", { defaultValue: "Modifica Intermediario" })?.toUpperCase()
                    : t("intermediaries.wizard.title")?.toUpperCase()
            }
            subtitle={t("intermediaries.wizard.subtitle")}
            sx={{
                ...(isEmbed && {
                    minHeight: "calc(100vh - 32px)"
                })
            }}
        >
            <Grid container>
                <Grid
                    item
                    md={4}
                    xl={2.75}
                    sx={{
                        background: WhiteSmoke,
                        ...(isEmbed
                            ? {
                                  minHeight: "calc(100vh - 112px)"
                              }
                            : { minHeight: "calc(100vh - 160px)" })
                    }}
                >
                    {(loading || loadingIntermediary) && !data?.classifier ? (
                        <SidebarSkeleton withoutMenu />
                    ) : (
                        <DetailsSidebar
                            data={data}
                            customDetails={
                                <DetailsIntermediary
                                    intermediary={intermediary}
                                    loading={view === "CREATE" ? false : loading}
                                />
                            }
                            sections={intermediarySections(t)}
                            defaultSection={step}
                            withoutMenu={view === "CREATE"}
                            redirectUrl={`/details/${workspaceId}/intermediaries/${intermediaryTaxId}/edit`}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={8} xl={9.25} px={4} py={4}>
                    <IntermediaryStepForm
                        mode={view}
                        tenantItemId={tenant?.itemId}
                        step={step}
                        loading={loadingIntermediary}
                        linkToWorkspaceId={workspaceId}
                        intermediary={intermediary}
                        getIntermediary={() =>
                            fetchIntermediary({
                                url: `rnd:core:intermediary-registry-read:api://v1/${tenant?.itemId}/intermediary/${intermediaryTaxId}`,
                                params: { withSignatureImageFile: true, withCredentials: true }
                            }).then(res => {
                                setIntermediary(res?.data);
                            })
                        }
                        onTaxId={taxId => {
                            fetchIntermediary({
                                url: `rnd:core:intermediary-registry-read:api://v1/${tenant?.itemId}/intermediary/${taxId}`
                            })
                                .then(res => {
                                    setIntermediary(res?.data);
                                    stopPolling();
                                })
                                .catch(() => {
                                    startPolling(2000);
                                });
                        }}
                    />
                </Grid>
            </Grid>
        </OneBoxPage>
    );
};
